import React from 'react'
import { cn } from '@/lib/utils'
import { Plus } from 'lucide-react'
import { useOnline } from '@/hooks/useOnline'
import { OFFLINE_SHORT_REASON } from '@/components/ui/OfflineActions'

/**
 * @deprecated — accepted so existing callers compile, but ignored. The empty
 * state's icon chip is decorative, and pastels mean a phase of work
 * (lib/phaseTone), so the chip is always neutral glass.
 */
export type EmptyStateTone = 'mint' | 'peach' | 'lavender' | 'butter' | 'blush' | 'sky' | 'neutral'

/** Neutral glass icon chip — the same for every tone. */
const ICON_CHIP_STYLE: React.CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: 9999,
  background: 'rgba(255,255,255,0.06)',
  border: '0.5px solid rgba(255,255,255,0.12)',
  color: 'rgba(255,255,255,0.70)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 20,
  flexShrink: 0,
}

interface EmptyStateProps {
  icon?: React.ReactNode
  title: string
  description?: string
  action?: {
    label: string
    onClick: () => void
    /** Leading icon. Default "+" (a create); pass `null` for none, e.g. "Back to …". */
    icon?: React.ReactNode | null
  }
  /** NEW — secondary ghost action rendered below primary */
  secondaryAction?: {
    label: string
    onClick: () => void
  }
  /** @deprecated — ignored; the icon chip is always neutral glass. Kept so
   *  existing callers that pass a tone still type-check. */
  tone?: EmptyStateTone
  className?: string
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  tone: _tone,
  className,
}: EmptyStateProps) {
  // Strip leading "+ " from action.label if present — we render the icon
  const actionLabel = action?.label.replace(/^\+\s*/, '') ?? ''
  // Offline, a create can't be saved: the button says so instead of failing
  // after the form is filled in (UX-018).
  const online = useOnline()
  const actionIcon = action?.icon === undefined ? <Plus style={{ width: 16, height: 16 }} /> : action.icon

  return (
    <div
      className={cn('flex flex-col items-center justify-center text-center', className)}
      style={{
        background: 'rgba(0,0,0,0.3)',
        border: '0.5px solid var(--midnight-border, rgba(255,255,255,0.08))',
        borderRadius: 'var(--midnight-r-lg, 24px)',
        padding: 40,
      }}
    >
      {icon && (
        <div style={ICON_CHIP_STYLE}>
          {/* Clone the icon to apply appropriate sizing if it's a Lucide element */}
          {React.isValidElement(icon)
            ? React.cloneElement(icon as React.ReactElement<{ width?: number; height?: number; style?: React.CSSProperties }>, {
                width: 24,
                height: 24,
                style: { flexShrink: 0 },
              })
            : icon}
        </div>
      )}

      <h3
        style={{
          margin: '0 0 8px',
          fontSize: 20,
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: 'var(--midnight-text, #FFFFFF)',
        }}
      >
        {title}
      </h3>

      {description && (
        <p
          style={{
            margin: '0 0 24px',
            fontSize: 15,
            lineHeight: 1.5,
            color: 'var(--midnight-text2, rgba(255,255,255,0.60))',
            maxWidth: 320,
          }}
        >
          {description}
        </p>
      )}

      {action && (
        <button
          type="button"
          onClick={action.onClick}
          disabled={!online}
          title={online ? undefined : OFFLINE_SHORT_REASON}
          data-needs-network=""
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 44,
            padding: '0 20px',
            fontSize: 15,
            fontWeight: 600,
            borderRadius: 9999,
            background: '#FFFFFF',
            color: '#000000',
            border: 'none',
            cursor: online ? 'pointer' : 'not-allowed',
            opacity: online ? 1 : 0.45,
            transition: 'opacity 0.2s ease',
            marginBottom: secondaryAction ? 8 : 0,
          }}
          onMouseEnter={(e) => { if (online) (e.currentTarget as HTMLButtonElement).style.opacity = '0.88' }}
          onMouseLeave={(e) => { if (online) (e.currentTarget as HTMLButtonElement).style.opacity = '1' }}
        >
          {actionIcon}
          {actionLabel}
        </button>
      )}

      {secondaryAction && (
        <button
          type="button"
          onClick={secondaryAction.onClick}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 40,
            padding: '0 16px',
            fontSize: 14,
            fontWeight: 500,
            borderRadius: 9999,
            background: 'transparent',
            color: 'var(--midnight-text2, rgba(255,255,255,0.60))',
            border: '0.5px solid var(--midnight-border, rgba(255,255,255,0.08))',
            cursor: 'pointer',
            transition: 'color 0.2s ease, border-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLButtonElement
            el.style.color = '#FFFFFF'
            el.style.borderColor = 'var(--midnight-border-bright, rgba(255,255,255,0.14))'
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLButtonElement
            el.style.color = 'var(--midnight-text2, rgba(255,255,255,0.60))'
            el.style.borderColor = 'var(--midnight-border, rgba(255,255,255,0.08))'
          }}
        >
          {secondaryAction.label}
        </button>
      )}
    </div>
  )
}
